/**
 * Switch Devices During a Session (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Switch Devices During a Session",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "You can change your microphone, Other’s audio and the device each translation plays on without stopping the session, for example when you plug in a headset."
  },
  "steps": [
    {
      "title": "Open Settings during the session",
      "content": "Most settings are locked while a session runs, and Settings says so at the top. Your devices are not: pick another <strong>Microphone</strong>, change <strong>Noise Suppression</strong>, or pick another <strong>Other’s audio</strong> source (desktop app), and the session carries on. In the <strong>Speech</strong> block, every row that plays sound has its own output: <strong>Default playback device</strong>, <strong>I hear it too</strong> and <strong>Translation I hear</strong>, and in a face-to-face conversation <strong>Translation the other side hears</strong>. You can change these devices, and the left or right channel of a translation, during the session too.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Or use the mode button",
      "content": "Click the selected mode at the bottom left to switch devices from the main window. The rows depend on the mode: <strong>Me</strong> shows your microphone and I hear it too, <strong>Other</strong> shows Other’s audio and Translation I hear, and <strong>Both</strong> shows your microphone and Other’s audio. With the other person beside you, Both shows your microphone, Translation the other side hears and Translation I hear, each with a preview button."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "If switching Other’s audio fails, Sokuji keeps the previous source. Stop and start the session to change it.",
      "The mode, the other side, the languages, the provider, Translation the other side hears and Translation I hear stay locked until you stop the session. Those two rows say “Fixed for this session; stop it to change.”",
      "In the browser extension, the meeting tab’s own sound keeps playing on the Default playback device chosen when the session started."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Why is a setting greyed out during the session?",
        "answer": "Devices, channels, noise suppression, I hear it too, Passthrough and its volume, and Keep spoken translations for replay can change live. Stop the session to change anything else."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "在会话期间切换设备",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "你可以在不停止会话的情况下更换麦克风、对方音频，以及每路翻译播放的设备，例如在插上耳机时。"
  },
  "steps": [
    {
      "title": "在会话期间打开设置",
      "content": "会话运行期间，大多数设置都会被锁定，设置顶部也会给出提示。但设备不受限制：选择其他<strong>麦克风</strong>、更改<strong>噪声抑制</strong>，或者选择其他<strong>对方音频</strong>来源（桌面应用），会话都会继续进行。在<strong>语音</strong>区块中，每一行会播放声音的设置都有自己的输出：<strong>默认播放设备</strong>、<strong>我也听</strong>和<strong>我听到的翻译</strong>，面对面对话时还有<strong>对方听到的翻译</strong>。这些设备，以及每路翻译的左右声道，也都可以在会话期间更改。",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "或者使用模式按钮",
      "content": "点击左下角当前选中的模式，即可在主窗口中切换设备。显示哪些行取决于模式：<strong>我</strong>显示麦克风和“我也听”，<strong>对方</strong>显示对方音频和“我听到的翻译”，<strong>两者</strong>显示麦克风和对方音频。对方就在你身边时，“两者”显示麦克风、“对方听到的翻译”和“我听到的翻译”，每行都有一个试听按钮。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "如果切换对方音频失败，Sokuji 会保留之前的来源。停止并重新开始会话即可更改。",
      "模式、对方在哪里、语言、服务商、“对方听到的翻译”和“我听到的翻译”会一直保持锁定，直到你停止会话。这两行会显示“本次会话已固定，停止后可改。”",
      "在浏览器扩展中，会议标签页本身的声音会继续在会话开始时选定的默认播放设备上播放。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "为什么会话期间某个设置是灰色的？",
        "answer": "设备、声道、噪声抑制、“我也听”、原声直通及其音量，以及“保留译音以便回放”都可以实时更改。要更改其他任何设置，请停止会话。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "セッション中にデバイスを切り替える",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "ヘッドセットを接続したときなど、セッションを停止せずに、マイク、相手の音声、各翻訳を再生するデバイスを変更できます。"
  },
  "steps": [
    {
      "title": "セッション中に設定を開く",
      "content": "セッションの実行中はほとんどの設定がロックされ、設定の上部にもその旨が表示されます。ただしデバイスはロックされません。別の<strong>マイクロフォン</strong>を選んだり、<strong>ノイズ抑制</strong>を変更したり、別の<strong>相手の音声</strong>のソース（デスクトップアプリ）を選んだりしても、セッションはそのまま続きます。<strong>音声</strong>ブロックでは、音を再生する行ごとに出力先があります。<strong>既定の再生デバイス</strong>、<strong>自分にも再生</strong>、<strong>自分に聞こえる翻訳</strong>、そして対面での会話では<strong>相手に聞こえる翻訳</strong>です。これらのデバイスや、翻訳を再生する左右のチャンネルも、セッション中に変更できます。",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "モードボタンを使う",
      "content": "左下の選択中のモードをクリックすると、メイン画面からデバイスを切り替えられます。表示される行はモードによって異なります。<strong>自分</strong>モードではマイクと「自分にも再生」、<strong>相手</strong>モードでは相手の音声と「自分に聞こえる翻訳」、<strong>両方</strong>モードではマイクと相手の音声が表示されます。相手が隣にいる場合、両方モードではマイク、「相手に聞こえる翻訳」、「自分に聞こえる翻訳」が表示され、それぞれに試聴ボタンがあります。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "相手の音声の切り替えに失敗した場合、Sokuji は以前のソースを使い続けます。変更するにはセッションを停止して開始し直してください。",
      "モード、相手の場所、言語、プロバイダー、「相手に聞こえる翻訳」、「自分に聞こえる翻訳」は、セッションを停止するまでロックされたままです。この 2 つの行には「このセッションでは固定されています。変更するには停止してください。」と表示されます。",
      "ブラウザー拡張機能では、会議タブ自体の音は、セッション開始時に選んだ「既定の再生デバイス」でそのまま再生されます。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "セッション中に設定がグレーになっているのはなぜですか？",
        "answer": "デバイス、チャンネル、ノイズ抑制、「自分にも再生」、「原音パススルー」とその音量、「再生用に音声を保持」は、セッション中でも変更できます。それ以外を変更するにはセッションを停止してください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "세션 중에 장치 전환하기",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "헤드셋을 연결할 때처럼 세션을 중지하지 않고도 마이크, 상대방 오디오, 각 번역이 재생되는 장치를 바꿀 수 있습니다."
  },
  "steps": [
    {
      "title": "세션 중에 설정 열기",
      "content": "세션이 실행되는 동안 대부분의 설정은 잠기며, 설정 상단에도 그렇게 표시됩니다. 하지만 장치는 잠기지 않습니다. 다른 <strong>마이크</strong>를 고르거나 <strong>노이즈 억제</strong>를 바꾸거나 다른 <strong>상대방 오디오</strong> 소스(데스크톱 앱)를 골라도 세션은 계속됩니다. <strong>음성</strong> 블록에서는 소리를 재생하는 행마다 출력 장치가 따로 있습니다. <strong>기본 재생 장치</strong>, <strong>나도 듣기</strong>, <strong>내가 듣는 번역</strong>, 그리고 대면 대화에서는 <strong>상대방에게 들리는 번역</strong>입니다. 이 장치들과 번역이 재생되는 왼쪽·오른쪽 채널도 세션 중에 바꿀 수 있습니다.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "또는 모드 버튼 사용",
      "content": "왼쪽 아래의 선택된 모드를 클릭하면 메인 창에서 장치를 전환할 수 있습니다. 표시되는 행은 모드에 따라 다릅니다. <strong>나</strong> 모드에서는 마이크와 “나도 듣기”, <strong>상대방</strong> 모드에서는 상대방 오디오와 “내가 듣는 번역”, <strong>모두</strong> 모드에서는 마이크와 상대방 오디오가 표시됩니다. 상대방이 내 옆에 있으면 모두 모드에서 마이크, “상대방에게 들리는 번역”, “내가 듣는 번역”이 표시되며, 각각 미리 듣기 버튼이 있습니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "상대방 오디오 전환에 실패하면 Sokuji는 이전 소스를 유지합니다. 바꾸려면 세션을 중지했다가 다시 시작하세요.",
      "모드, 상대방 위치, 언어, 제공업체, “상대방에게 들리는 번역”, “내가 듣는 번역”은 세션을 중지할 때까지 잠겨 있습니다. 이 두 행에는 “이 세션에서는 고정되어 있습니다. 바꾸려면 중지하세요.”라고 표시됩니다.",
      "브라우저 확장 프로그램에서는 회의 탭 자체의 소리가 세션을 시작할 때 선택한 “기본 재생 장치”에서 계속 재생됩니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "세션 중에 설정이 회색으로 표시되는 이유는 무엇인가요?",
        "answer": "장치, 채널, 노이즈 억제, “나도 듣기”, “원음 패스스루”와 그 음량, “다시 듣기용으로 번역 음성 보관”은 세션 중에도 바꿀 수 있습니다. 그 밖의 설정을 바꾸려면 세션을 중지하세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Cambiar de dispositivo durante una sesión",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Puedes cambiar tu micrófono, el Audio del otro y el dispositivo en el que suena cada traducción sin detener la sesión, por ejemplo al conectar unos auriculares."
  },
  "steps": [
    {
      "title": "Abre Configuración durante la sesión",
      "content": "La mayoría de los ajustes están bloqueados mientras hay una sesión en marcha, y Configuración lo indica arriba. Tus dispositivos no: elige otro <strong>Micrófono</strong>, cambia la <strong>Supresión de ruido</strong> o elige otra fuente de <strong>Audio del otro</strong> (aplicación de escritorio), y la sesión continúa. En el bloque <strong>Voz</strong>, cada fila que reproduce sonido tiene su propia salida: <strong>Dispositivo de reproducción predeterminado</strong>, <strong>Yo también la oigo</strong> y <strong>Traducción que yo oigo</strong> y, en una conversación cara a cara, <strong>Traducción que oye la otra parte</strong>. También puedes cambiar estos dispositivos, y el canal izquierdo o derecho de una traducción, durante la sesión.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "O usa el botón de modo",
      "content": "Haz clic en el modo seleccionado, abajo a la izquierda, para cambiar de dispositivo desde la ventana principal. Las filas dependen del modo: <strong>Yo</strong> muestra tu micrófono y Yo también la oigo, <strong>Otro</strong> muestra el Audio del otro y Traducción que yo oigo, y <strong>Ambos</strong> muestra tu micrófono y el Audio del otro. Con la otra persona a tu lado, Ambos muestra tu micrófono, Traducción que oye la otra parte y Traducción que yo oigo, cada una con un botón para escucharla."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Si falla el cambio del Audio del otro, Sokuji mantiene la fuente anterior. Detén e inicia la sesión para cambiarla.",
      "El modo, dónde está el otro, los idiomas, el proveedor, Traducción que oye la otra parte y Traducción que yo oigo siguen bloqueados hasta que detengas la sesión. Esas dos filas indican «Fijado para esta sesión; detén la sesión para cambiarlo.»",
      "En la extensión de navegador, el sonido propio de la pestaña de la reunión se sigue reproduciendo en el Dispositivo de reproducción predeterminado elegido al iniciar la sesión."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Por qué aparece un ajuste en gris durante la sesión?",
        "answer": "Los dispositivos, los canales, la supresión de ruido, Yo también la oigo, Paso directo y su volumen, y Conservar las traducciones habladas para reproducirlas pueden cambiar en directo. Detén la sesión para cambiar cualquier otra cosa."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Changer de périphérique pendant une session",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Vous pouvez changer de microphone, d’Audio de l'autre et du périphérique sur lequel chaque traduction est lue sans arrêter la session, par exemple quand vous branchez un casque."
  },
  "steps": [
    {
      "title": "Ouvrez les Paramètres pendant la session",
      "content": "La plupart des réglages sont verrouillés pendant une session, et les Paramètres l’indiquent en haut. Vos périphériques ne le sont pas : choisissez un autre <strong>Microphone</strong>, modifiez la <strong>Suppression du bruit</strong> ou choisissez une autre source d’<strong>Audio de l'autre</strong> (application de bureau), et la session continue. Dans le bloc <strong>Voix</strong>, chaque ligne qui émet du son a sa propre sortie : <strong>Appareil de lecture par défaut</strong>, <strong>Je l'entends aussi</strong> et <strong>Traduction que j'entends</strong>, et, dans une conversation en face à face, <strong>Traduction entendue par l'autre</strong>. Vous pouvez aussi changer ces périphériques, et le canal gauche ou droit d’une traduction, pendant la session.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Ou utilisez le bouton de mode",
      "content": "Cliquez sur le mode sélectionné en bas à gauche pour changer de périphérique depuis la fenêtre principale. Les lignes dépendent du mode : <strong>Moi</strong> affiche votre microphone et Je l'entends aussi, <strong>Autre</strong> affiche l’Audio de l'autre et Traduction que j'entends, et <strong>Les deux</strong> affiche votre microphone et l’Audio de l'autre. Quand l’autre personne est à côté de vous, Les deux affiche votre microphone, Traduction entendue par l'autre et Traduction que j'entends, chaque ligne avec un bouton d’écoute."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Si le changement d’Audio de l'autre échoue, Sokuji conserve la source précédente. Arrêtez puis relancez la session pour la changer.",
      "Le mode, l’endroit où se trouve votre interlocuteur, les langues, le fournisseur, Traduction entendue par l'autre et Traduction que j'entends restent verrouillés jusqu’à l’arrêt de la session. Ces deux lignes indiquent « Fixé pour cette session ; arrêtez-la pour modifier. »",
      "Dans l’extension de navigateur, le son de l’onglet de la réunion continue d’être lu sur l’Appareil de lecture par défaut choisi au démarrage de la session."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Pourquoi un réglage est-il grisé pendant la session ?",
        "answer": "Les périphériques, les canaux, la suppression du bruit, Je l'entends aussi, Passage direct et son volume, ainsi que Conserver les traductions parlées pour les réécouter, peuvent changer en direct. Arrêtez la session pour modifier autre chose."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "تبديل الأجهزة أثناء الجلسة",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يمكنك تغيير الميكروفون وصوت الآخر والجهاز الذي تُشغَّل عليه كل ترجمة دون إيقاف الجلسة، مثلًا عندما توصل سماعة رأس."
  },
  "steps": [
    {
      "title": "افتح الإعدادات أثناء الجلسة",
      "content": "تكون معظم الإعدادات مقفلة أثناء تشغيل الجلسة، وتشير الإعدادات إلى ذلك في أعلاها. أما أجهزتك فليست مقفلة: اختر <strong>ميكروفون</strong> آخر، أو غيّر <strong>قمع الضوضاء</strong>، أو اختر مصدر <strong>صوت الآخر</strong> (تطبيق سطح المكتب) آخر، وستستمر الجلسة. في قسم <strong>الكلام</strong>، لكل صف يشغّل صوتًا مخرجه الخاص: <strong>جهاز التشغيل الافتراضي</strong> و<strong>أسمعها أنا أيضًا</strong> و<strong>الترجمة التي أسمعها</strong>، وفي المحادثة وجهًا لوجه <strong>الترجمة التي يسمعها الطرف الآخر</strong>. يمكنك أيضًا تغيير هذه الأجهزة، والقناة اليسرى أو اليمنى للترجمة، أثناء الجلسة.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "أو استخدم زر الوضع",
      "content": "انقر على الوضع المحدد أسفل اليسار لتبديل الأجهزة من النافذة الرئيسية. تختلف الصفوف حسب الوضع: يعرض وضع <strong>أنا</strong> ميكروفونك و«أسمعها أنا أيضًا»، ويعرض وضع <strong>الآخر</strong> صوت الآخر و«الترجمة التي أسمعها»، ويعرض وضع <strong>الكل</strong> ميكروفونك وصوت الآخر. وعندما يكون الشخص الآخر بجانبك، يعرض وضع الكل ميكروفونك و«الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها»، ولكل منها زر معاينة."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "إذا فشل تبديل صوت الآخر، يحتفظ Sokuji بالمصدر السابق. أوقف الجلسة وابدأها من جديد لتغييره.",
      "يبقى الوضع ومكان الطرف الآخر واللغات ومزوّد الخدمة و«الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» مقفلة حتى توقف الجلسة. ويظهر في هذين الصفين: «مثبّت لهذه الجلسة؛ أوقفها لتغييره.»",
      "في امتداد المتصفح، يستمر صوت علامة تبويب الاجتماع نفسها في التشغيل على جهاز التشغيل الافتراضي الذي كان مختارًا عند بدء الجلسة."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "لماذا يظهر أحد الإعدادات باللون الرمادي أثناء الجلسة؟",
        "answer": "يمكن تغيير الأجهزة والقنوات وقمع الضوضاء و«أسمعها أنا أيضًا» و«التمرير المباشر» ومستوى صوته و«الاحتفاظ بالترجمات المنطوقة لإعادة التشغيل» أثناء الجلسة. أوقف الجلسة لتغيير أي شيء آخر."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Trocar de dispositivo durante uma sessão",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Você pode trocar o microfone, o Áudio do outro e o dispositivo em que cada tradução toca sem encerrar a sessão, por exemplo ao conectar um headset."
  },
  "steps": [
    {
      "title": "Abra as Configurações durante a sessão",
      "content": "A maioria das configurações fica bloqueada durante uma sessão, e as Configurações avisam isso no topo. Os seus dispositivos não: escolha outro <strong>Microfone</strong>, altere a <strong>Supressão de Ruído</strong> ou escolha outra fonte de <strong>Áudio do outro</strong> (aplicativo de desktop), e a sessão continua. No bloco <strong>Voz</strong>, cada linha que reproduz som tem a sua própria saída: <strong>Dispositivo de reprodução padrão</strong>, <strong>Eu também ouço</strong> e <strong>Tradução que eu ouço</strong> e, em uma conversa cara a cara, <strong>Tradução que o outro lado ouve</strong>. Você também pode trocar esses dispositivos, e o canal esquerdo ou direito de uma tradução, durante a sessão.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Ou use o botão de modo",
      "content": "Clique no modo selecionado, no canto inferior esquerdo, para trocar de dispositivo pela janela principal. As linhas dependem do modo: <strong>Eu</strong> mostra o seu microfone e Eu também ouço, <strong>Outro</strong> mostra o Áudio do outro e Tradução que eu ouço, e <strong>Ambos</strong> mostra o seu microfone e o Áudio do outro. Com a outra pessoa ao seu lado, Ambos mostra o seu microfone, Tradução que o outro lado ouve e Tradução que eu ouço, cada uma com um botão para ouvir uma prévia."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Se a troca do Áudio do outro falhar, o Sokuji mantém a fonte anterior. Encerre e inicie a sessão para trocá-la.",
      "O modo, onde está o outro, os idiomas, o provedor, Tradução que o outro lado ouve e Tradução que eu ouço continuam bloqueados até você encerrar a sessão. Essas duas linhas dizem “Fixo nesta sessão; pare-a para alterar.”",
      "Na extensão de navegador, o som da própria aba da reunião continua tocando no Dispositivo de reprodução padrão escolhido quando a sessão começou."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Por que uma configuração aparece esmaecida durante a sessão?",
        "answer": "Os dispositivos, os canais, a supressão de ruído, Eu também ouço, Passagem direta e o volume dela, e Manter traduções faladas para reprodução podem ser alterados ao vivo. Encerre a sessão para alterar qualquer outra coisa."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Смена устройств во время сеанса",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Можно сменить микрофон, аудио собеседника и устройство, на котором звучит каждый перевод, не останавливая сеанс, например когда вы подключаете гарнитуру."
  },
  "steps": [
    {
      "title": "Откройте настройки во время сеанса",
      "content": "Пока сеанс запущен, большинство настроек заблокировано, о чём сообщается вверху настроек. Устройства — нет: выберите другой <strong>Микрофон</strong>, измените <strong>Шумоподавление</strong> или выберите другой источник <strong>Аудио собеседника</strong> (настольное приложение), и сеанс продолжится. В блоке <strong>Речь</strong> у каждой строки, которая воспроизводит звук, свой выход: <strong>Устройство воспроизведения по умолчанию</strong>, <strong>Я тоже слышу</strong> и <strong>Перевод, который слышу я</strong>, а при разговоре лицом к лицу — <strong>Перевод, который слышит собеседник</strong>. Эти устройства, а также левый или правый канал перевода, тоже можно менять во время сеанса.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Или используйте кнопку режима",
      "content": "Нажмите выбранный режим в левом нижнем углу, чтобы сменить устройства прямо из главного окна. Строки зависят от режима: <strong>Я</strong> показывает ваш микрофон и «Я тоже слышу», <strong>Собеседник</strong> — аудио собеседника и «Перевод, который слышу я», а <strong>Оба</strong> — ваш микрофон и аудио собеседника. Когда собеседник рядом с вами, «Оба» показывает ваш микрофон, «Перевод, который слышит собеседник» и «Перевод, который слышу я», и у каждой строки есть кнопка прослушивания."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Если сменить аудио собеседника не удалось, Sokuji оставляет прежний источник. Чтобы сменить его, остановите и снова запустите сеанс.",
      "Режим, параметр «Где собеседник», языки, провайдер, «Перевод, который слышит собеседник» и «Перевод, который слышу я» остаются заблокированными, пока вы не остановите сеанс. На этих двух строках написано: «Зафиксировано на эту сессию; остановите её, чтобы изменить.»",
      "В расширении для браузера собственный звук вкладки встречи продолжает воспроизводиться через «Устройство воспроизведения по умолчанию», выбранное при запуске сеанса."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Почему настройка недоступна во время сеанса?",
        "answer": "На лету можно менять устройства, каналы, шумоподавление, «Я тоже слышу», «Passthrough» и его громкость, а также «Сохранять озвученные переводы для повтора». Чтобы изменить что-то ещё, остановите сеанс."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Geräte während einer Sitzung wechseln",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Sie können Ihr Mikrofon, das Audio des Gegenübers und das Gerät, auf dem jede Übersetzung abgespielt wird, wechseln, ohne die Sitzung zu beenden, zum Beispiel wenn Sie ein Headset anschließen."
  },
  "steps": [
    {
      "title": "Die Einstellungen während der Sitzung öffnen",
      "content": "Die meisten Einstellungen sind gesperrt, solange eine Sitzung läuft, und die Einstellungen weisen oben darauf hin. Ihre Geräte nicht: Wählen Sie ein anderes <strong>Mikrofon</strong>, ändern Sie die <strong>Rauschunterdrückung</strong> oder wählen Sie eine andere Quelle für <strong>Audio des Gegenübers</strong> (Desktop-App), und die Sitzung läuft weiter. Im Bereich <strong>Sprache</strong> hat jede Zeile, die Ton wiedergibt, ihre eigene Ausgabe: <strong>Standard-Wiedergabegerät</strong>, <strong>Ich höre es auch</strong> und <strong>Übersetzung, die ich höre</strong> sowie in einem Gespräch von Angesicht zu Angesicht <strong>Übersetzung, die das Gegenüber hört</strong>. Auch diese Geräte und den linken oder rechten Kanal einer Übersetzung können Sie während der Sitzung ändern.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Oder die Modus-Schaltfläche verwenden",
      "content": "Klicken Sie unten links auf den ausgewählten Modus, um Geräte direkt im Hauptfenster zu wechseln. Welche Zeilen erscheinen, hängt vom Modus ab: <strong>Ich</strong> zeigt Ihr Mikrofon und „Ich höre es auch“, <strong>Gegenüber</strong> zeigt das Audio des Gegenübers und „Übersetzung, die ich höre“, und <strong>Beides</strong> zeigt Ihr Mikrofon und das Audio des Gegenübers. Sitzt die andere Person neben Ihnen, zeigt „Beides“ Ihr Mikrofon, „Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“, jeweils mit einer Schaltfläche zum Vorhören."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Schlägt der Wechsel des Audios des Gegenübers fehl, behält Sokuji die vorherige Quelle. Beenden und starten Sie die Sitzung, um sie zu ändern.",
      "Der Modus, die Angabe, wo das Gegenüber ist, die Sprachen, der Anbieter, „Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“ bleiben gesperrt, bis Sie die Sitzung beenden. Diese beiden Zeilen zeigen dann „Für diese Sitzung festgelegt; zum Ändern stoppen.“",
      "In der Browsererweiterung läuft der Ton des Meeting-Tabs selbst weiter über das Standard-Wiedergabegerät, das beim Start der Sitzung gewählt war."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Warum ist eine Einstellung während der Sitzung ausgegraut?",
        "answer": "Geräte, Kanäle, Rauschunterdrückung, „Ich höre es auch“, „Durchleitung“ und ihre Lautstärke sowie „Gesprochene Übersetzungen zur Wiedergabe behalten“ lassen sich im laufenden Betrieb ändern. Beenden Sie die Sitzung, um etwas anderes zu ändern."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Зміна пристроїв під час сеансу",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Можна змінити мікрофон, аудіо співрозмовника і пристрій, на якому звучить кожен переклад, не зупиняючи сеанс, наприклад коли ви підключаєте гарнітуру."
  },
  "steps": [
    {
      "title": "Відкрийте налаштування під час сеансу",
      "content": "Поки сеанс запущено, більшість налаштувань заблоковано, про що повідомляється вгорі налаштувань. Пристрої — ні: виберіть інший <strong>Мікрофон</strong>, змініть <strong>Придушення шуму</strong> або виберіть інше джерело <strong>Аудіо співрозмовника</strong> (настільний застосунок), і сеанс триватиме далі. У блоці <strong>Мовлення</strong> кожен рядок, що відтворює звук, має власний вихід: <strong>Пристрій відтворення за замовчуванням</strong>, <strong>Я теж чую</strong> і <strong>Переклад, який чую я</strong>, а під час розмови віч-на-віч — <strong>Переклад, який чує співрозмовник</strong>. Ці пристрої, а також лівий чи правий канал перекладу, теж можна змінювати під час сеансу.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Або скористайтеся кнопкою режиму",
      "content": "Натисніть вибраний режим у лівому нижньому куті, щоб змінити пристрої просто з головного вікна. Рядки залежать від режиму: <strong>Я</strong> показує ваш мікрофон і «Я теж чую», <strong>Співрозмовник</strong> — аудіо співрозмовника і «Переклад, який чую я», а <strong>Обидва</strong> — ваш мікрофон і аудіо співрозмовника. Коли співрозмовник поруч із вами, «Обидва» показує ваш мікрофон, «Переклад, який чує співрозмовник» і «Переклад, який чую я», і кожен рядок має кнопку прослуховування."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Якщо змінити аудіо співрозмовника не вдалося, Sokuji залишає попереднє джерело. Щоб змінити його, зупиніть і знову запустіть сеанс.",
      "Режим, параметр «Де співрозмовник», мови, провайдер, «Переклад, який чує співрозмовник» і «Переклад, який чую я» залишаються заблокованими, доки ви не зупините сеанс. На цих двох рядках написано: «Зафіксовано на цю сесію; зупиніть її, щоб змінити.»",
      "У розширенні для браузера власний звук вкладки зустрічі й далі відтворюється через «Пристрій відтворення за замовчуванням», вибраний під час запуску сеансу."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чому налаштування недоступне під час сеансу?",
        "answer": "На льоту можна змінювати пристрої, канали, придушення шуму, «Я теж чую», «Passthrough» і його гучність, а також «Зберігати озвучені переклади для повтору». Щоб змінити щось інше, зупиніть сеанс."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Cambiare dispositivo durante una sessione",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Puoi cambiare microfono, Audio dell'altro e il dispositivo su cui viene riprodotta ogni traduzione senza interrompere la sessione, per esempio quando colleghi delle cuffie."
  },
  "steps": [
    {
      "title": "Apri Impostazioni durante la sessione",
      "content": "Mentre una sessione è attiva, la maggior parte delle impostazioni è bloccata, e Impostazioni lo segnala in alto. I tuoi dispositivi no: scegli un altro <strong>Microfono</strong>, cambia la <strong>Soppressione del rumore</strong> o scegli un’altra sorgente per <strong>Audio dell'altro</strong> (app desktop), e la sessione prosegue. Nel blocco <strong>Voce</strong>, ogni riga che riproduce audio ha la propria uscita: <strong>Dispositivo di riproduzione predefinito</strong>, <strong>Lo sento anche io</strong> e <strong>Traduzione che sento io</strong> e, in una conversazione faccia a faccia, <strong>Traduzione che sente l'altra parte</strong>. Anche questi dispositivi, e il canale sinistro o destro di una traduzione, si possono cambiare durante la sessione.",
      "screenshot": "01-settings-during-session.png"
    },
    {
      "title": "Oppure usa il pulsante della modalità",
      "content": "Fai clic sulla modalità selezionata in basso a sinistra per cambiare dispositivo dalla finestra principale. Le righe dipendono dalla modalità: <strong>Io</strong> mostra il tuo microfono e Lo sento anche io, <strong>Altro</strong> mostra l’Audio dell'altro e Traduzione che sento io, ed <strong>Entrambi</strong> mostra il tuo microfono e l’Audio dell'altro. Con l’altra persona accanto a te, Entrambi mostra il tuo microfono, Traduzione che sente l'altra parte e Traduzione che sento io, ciascuna riga con un pulsante di anteprima."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Se il cambio dell’Audio dell'altro non riesce, Sokuji mantiene la sorgente precedente. Interrompi e riavvia la sessione per cambiarla.",
      "Modalità, posizione dell’altra parte, lingue, fornitore, Traduzione che sente l'altra parte e Traduzione che sento io restano bloccati finché non interrompi la sessione. Queste due righe indicano «Fissato per questa sessione; fermala per cambiarlo.»",
      "Nell’estensione per browser, l’audio della scheda della riunione continua a essere riprodotto sul Dispositivo di riproduzione predefinito scelto all’avvio della sessione."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Perché un’impostazione è disattivata durante la sessione?",
        "answer": "Dispositivi, canali, soppressione del rumore, Lo sento anche io, Passthrough e il suo volume, e Conserva le traduzioni parlate per la riproduzione possono cambiare in tempo reale. Interrompi la sessione per cambiare qualsiasi altra cosa."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureSwitchDevices() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/switch-devices" />;
}
